import React, { useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import ExpandableTextarea from '../../../components/ExpandableTextarea';
import { todayKST, toDateString } from '../../../utils/date';
import { Field, Modal } from './_modal-helpers';
import { PaymentRow, RefundRow, ServiceRow, Summary } from './RevenueEventModal.components';
import MobileRevenueEventLayout from './RevenueEventModal.mobile';
import { mobileBodyStyle } from './RevenueEventModal.mobileStyles';
import {
  EVENT_TYPE_OPTS,
  buildCustomerPayload,
  buildServicePayload,
  createAdditionalFlightRow,
  createAdditionalHotelRow,
  emptyItem,
  emptyPayment,
  emptyRefund,
  fmt,
  hasValue,
  initialOtherItems,
  initialPayments,
  initialRefunds,
  initialServiceRows,
  num,
  paymentTotal,
  refundTotal,
  summarize,
} from './RevenueEventModal.helpers';
import {
  compactShell,
  emptyBox,
  eventHeaderGrid,
  modalBodyCompact,
  miniBtn,
  paymentListStyle,
  paymentRowStyle,
  paymentSummary,
  refundRowStyle,
  revenueBodyGrid,
  sectionActions,
  sectionHeader,
  sectionHint,
  sectionStyle,
  sectionTitle,
  summaryBar,
  supplierHead,
  supplierTable,
  supplierTableWrap,
} from './RevenueEventModal.styles';

export default function RevenueEventModal({ travelCode, row, baseline = {}, sourceFlights = [], sourceHotels = [], masterNotes = '', isMobile = false, onClose, onSaved }) {
  const isNew = !row;
  const initialRowsForMode = useMemo(
    () => initialServiceRows(row, baseline, sourceFlights, sourceHotels),
    [row, baseline, sourceFlights, sourceHotels]
  );
  const [form, setForm] = useState(row ? {
    event_date: toDateString(row.event_date),
    event_type: row.event_type || 'change',
  } : {
    event_date: todayKST(),
    event_type: 'change',
  });
  const [reservationNotes, setReservationNotes] = useState(masterNotes || '');
  const [serviceRows, setServiceRows] = useState(() => initialRowsForMode);
  const [{ customer, misc }] = useState(() => initialOtherItems(row, baseline));
  const [payments, setPayments] = useState(() => initialPayments(row, row ? null : todayKST()));
  const [refunds, setRefunds] = useState(() => initialRefunds(row, row ? null : todayKST()));
  const [saving, setSaving] = useState(false);

  const linkedPaymentTotal = paymentTotal(payments);
  const linkedRefundTotal = refundTotal(refunds);
  const customerWithLedger = useMemo(
    () => ({ ...customer, customer_additional_payment: linkedPaymentTotal, customer_refund: linkedRefundTotal }),
    [customer, linkedPaymentTotal, linkedRefundTotal]
  );
  const preview = useMemo(() => summarize(serviceRows, customerWithLedger, misc), [serviceRows, customerWithLedger, misc]);
  const customerBalance = num(customer.target_cost) + linkedPaymentTotal - linkedRefundTotal;
  const fixedFlightCount = Math.max(sourceFlights.length, 1);
  const fixedHotelCount = Math.max(initialRowsForMode.hotel.filter((item) => !item.is_additional).length, 1);

  const upd = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  function updateService(type, index, key, value) {
    setServiceRows((prev) => ({
      ...prev,
      [type]: prev[type].map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)),
    }));
  }

  function addFlightRow() {
    setServiceRows((prev) => ({
      ...prev,
      flight: [...prev.flight, createAdditionalFlightRow(prev.flight.length)],
    }));
  }

  function removeFlightRow(index) {
    setServiceRows((prev) => {
      const nextRows = prev.flight.filter((_, itemIndex) => itemIndex !== index);
      return { ...prev, flight: nextRows.length ? nextRows : [emptyItem('flight', '항공 #1', 0)] };
    });
  }

  function addHotelRow() {
    setServiceRows((prev) => ({
      ...prev,
      hotel: [...prev.hotel, createAdditionalHotelRow(prev.hotel.length)],
    }));
  }

  function removeHotelRow(index) {
    setServiceRows((prev) => {
      const nextRows = prev.hotel.filter((_, itemIndex) => itemIndex !== index);
      return { ...prev, hotel: nextRows.length ? nextRows : [emptyItem('hotel', '호텔 · 1박', 0)] };
    });
  }

  function addPayment() {
    setPayments((prev) => ([...prev, emptyPayment(form.event_date || todayKST(), prev.length)]));
  }

  function updatePayment(index, key, value) {
    setPayments((prev) => prev.map((payment, itemIndex) => {
      if (itemIndex !== index) return payment;
      const next = { ...payment, [key]: value };
      if (key === 'payment_method' && value === 'cash' && !hasValue(next.cash_receipt_amount)) next.cash_receipt_amount = next.amount;
      if (key === 'amount' && next.payment_method === 'cash') next.cash_receipt_amount = value;
      if (key === 'payment_method' && value === 'card') {
        next.cash_receipt_issue_no = '';
        next.cash_receipt_amount = null;
      }
      return next;
    }));
  }

  function removePayment(index) {
    const target = payments[index];
    if (target?.card_deposit_status === 'confirmed') {
      alert('카드입금 확인된 결제는 삭제할 수 없습니다.');
      return;
    }
    setPayments((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  }

  function addRefund() {
    setRefunds((prev) => ([...prev, emptyRefund(form.event_date || todayKST(), prev.length)]));
  }

  function updateRefund(index, key, value) {
    setRefunds((prev) => prev.map((refund, itemIndex) => {
      if (itemIndex !== index) return refund;
      const next = { ...refund, [key]: value };
      if (key === 'refund_method' && value === 'cash' && !hasValue(next.cash_receipt_cancel_amount)) next.cash_receipt_cancel_amount = next.amount;
      if (key === 'amount' && next.refund_method === 'cash') next.cash_receipt_cancel_amount = value;
      if (key === 'refund_method' && value === 'card') {
        next.cash_receipt_cancel_approval_no = '';
        next.cash_receipt_cancel_amount = null;
      }
      return next;
    }));
  }

  function removeRefund(index) {
    setRefunds((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  }

  function hasAddedFlightInput(item = {}) {
    return [
      'supplier_refund_base_fare',
      'supplier_refund_fuel_surcharge',
      'supplier_refund_taxes',
      'additional_base_fare',
      'additional_fuel_surcharge',
      'additional_taxes',
      'supplier_refund',
      'additional_cost',
    ].some((key) => hasValue(item[key]) && num(item[key]) !== 0);
  }

  function validateBeforeSave() {
    const missingFlightLabel = serviceRows.flight.some((item) => (
      item.is_additional
      && hasAddedFlightInput(item)
      && !String(item.item_label || '').trim()
    ));
    if (missingFlightLabel) {
      alert('추가 항공에 금액을 입력했다면 구분을 입력해 주세요. 예: 성인 가는편, 홍길동 귀국편');
      return false;
    }
    for (const payment of payments) {
      if (num(payment.amount) === 0) continue;
      if (!payment.payment_date || !payment.payment_method) {
        alert('결제내역에 금액이 있으면 결제일과 지불수단을 입력해야 합니다.');
        return false;
      }
    }
    for (const refund of refunds) {
      if (num(refund.amount) === 0) continue;
      if (!refund.refund_date || !refund.refund_method) {
        alert('환불내역에 금액이 있으면 환불일과 환불수단을 입력해야 합니다.');
        return false;
      }
    }
    return true;
  }

  async function handleSave() {
    if (!validateBeforeSave()) return;
    setSaving(true);
    try {
      const payload = {
        event_date: form.event_date,
        event_type: form.event_type,
        calculation_mode: 'delta',
        reservation_notes: reservationNotes || null,
        items: [
          ...serviceRows.flight.map((item, index) => buildServicePayload(item, index)),
          buildServicePayload(serviceRows.car[0] || emptyItem('car'), 100),
          ...serviceRows.hotel.map((item, index) => buildServicePayload(item, 200 + index)),
          buildCustomerPayload(customerWithLedger),
          misc,
        ],
        payments: payments.map((payment, index) => ({ ...payment, sort_order: index })),
        refunds: refunds.map((refund, index) => ({ ...refund, sort_order: index })),
      };
      const res = isNew
        ? await apiClient.post(`/api/admin/reservations/${encodeURIComponent(travelCode)}/revenue-events`, payload)
        : await apiClient.put(`/api/admin/reservation-revenue-events/${row.id}`, payload);
      if (res.ok && res.data?.success) {
        onSaved && onSaved();
        onClose();
      } else {
        alert(res.data?.message || '저장 실패');
      }
    } catch (error) {
      alert('오류: ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isNew ? '수익 이벤트 추가' : `수익 이벤트 수정 #${row.id}`} onClose={onClose} onSave={handleSave} saving={saving} maxWidth={isMobile ? 520 : 1280} bodyStyle={isMobile ? mobileBodyStyle : modalBodyCompact}>
      {isMobile ? (
        <MobileRevenueEventLayout
          form={form}
          upd={upd}
          reservationNotes={reservationNotes}
          setReservationNotes={setReservationNotes}
          serviceRows={serviceRows}
          updateService={updateService}
          fixedFlightCount={fixedFlightCount}
          fixedHotelCount={fixedHotelCount}
          addFlightRow={addFlightRow}
          removeFlightRow={removeFlightRow}
          addHotelRow={addHotelRow}
          removeHotelRow={removeHotelRow}
          payments={payments}
          addPayment={addPayment}
          updatePayment={updatePayment}
          removePayment={removePayment}
          refunds={refunds}
          addRefund={addRefund}
          updateRefund={updateRefund}
          removeRefund={removeRefund}
          preview={preview}
          customer={customer}
          customerBalance={customerBalance}
          linkedPaymentTotal={linkedPaymentTotal}
          linkedRefundTotal={linkedRefundTotal}
        />
      ) : (
      <div style={compactShell}>
        <div style={eventHeaderGrid}>
          <Field label="수익 반영일" required type="date" value={form.event_date} onChange={upd('event_date')} />
          <Field label="유형" value={form.event_type} onChange={upd('event_type')} options={EVENT_TYPE_OPTS} />
          <div style={noteFieldWrap}>
            <span style={noteLabel}>예약 메모</span>
            <ExpandableTextarea
              value={reservationNotes}
              onChange={setReservationNotes}
              rows={2}
              expandedRows={6}
              style={noteTextarea}
            />
          </div>
        </div>

        <div style={summaryBar}>
          <Summary label="이번 달 반영" value={preview.amount} strong />
          <Summary label="기존 고객결제" value={customer.target_cost} />
          <Summary label="추가결제" value={linkedPaymentTotal} />
          <Summary label="고객환불" value={linkedRefundTotal} />
          <Summary label="고객 보유액" value={customerBalance} />
          <Summary label="공급사 환불" value={preview.serviceRefund} />
          <Summary label="추가 원가" value={preview.additionalCost} />
        </div>

        <div style={revenueBodyGrid}>
          <section style={sectionStyle}>
            <div style={sectionHeader}>
              <div>
                <div style={sectionTitle}>공급사 정산</div>
                <div style={sectionHint}>항공은 항공운임/유류할증료/공항세로 나누어 입력하고, 호텔은 1박당 기준으로 입력합니다.</div>
              </div>
              <div style={sectionActions}>
                <button type="button" style={miniBtn} onClick={addFlightRow}>+ 항공</button>
                <button type="button" style={miniBtn} onClick={addHotelRow}>+ 박 추가</button>
              </div>
            </div>
            <div style={supplierTableWrap}>
              <div style={supplierTable}>
                <div style={supplierHead}>구분</div>
                <div style={supplierHead}>기존 원가</div>
                <div style={supplierHead}>환불액</div>
                <div style={supplierHead}>취소수수료</div>
                <div style={supplierHead}>추가/재예약 원가</div>
                <div style={supplierHead}>반영</div>
                {serviceRows.flight.map((item, index) => (
                  <ServiceRow
                    key={`flight-${index}`}
                    label="항공"
                    item={item}
                    editableLabel={index >= fixedFlightCount}
                    canRemove={index >= fixedFlightCount}
                    onChange={(key, value) => updateService('flight', index, key, value)}
                    onRemove={() => removeFlightRow(index)}
                  />
                ))}
                <ServiceRow label="렌트" item={serviceRows.car[0] || emptyItem('car')} index={0} onChange={(key, value) => updateService('car', 0, key, value)} />
                {serviceRows.hotel.map((item, index) => (
                  <ServiceRow
                    key={`hotel-${index}`}
                    label="호텔"
                    item={item}
                    editableLabel={index >= fixedHotelCount}
                    canRemove={index >= fixedHotelCount}
                    onChange={(key, value) => updateService('hotel', index, key, value)}
                    onRemove={() => removeHotelRow(index)}
                  />
                ))}
              </div>
            </div>
          </section>
        </div>

        <section style={sectionStyle}>
          <div style={sectionHeader}>
            <div>
              <div style={sectionTitle}>추가 결제내역</div>
              <div style={sectionHint}>여기에 입력한 결제 금액 합계가 자동으로 추가 결제액이 됩니다.</div>
            </div>
            <div style={sectionActions}>
              <div style={paymentSummary(0)}>추가 결제 {fmt(linkedPaymentTotal)}</div>
              <button type="button" style={miniBtn} onClick={addPayment}>+ 결제 추가</button>
            </div>
          </div>
          <div style={paymentListStyle}>
            {payments.length === 0 ? (
              <div style={emptyBox}>추가 결제내역이 없습니다.</div>
            ) : payments.map((payment, index) => (
              <PaymentRow
                key={payment.id || index}
                payment={payment}
                index={index}
                onChange={updatePayment}
                onRemove={removePayment}
                style={paymentRowStyle}
              />
            ))}
          </div>
        </section>

        <section style={sectionStyle}>
          <div style={sectionHeader}>
            <div>
              <div style={sectionTitle}>환불내역</div>
              <div style={sectionHint}>환불수단과 현금영수증 취소승인번호를 함께 남깁니다.</div>
            </div>
            <div style={sectionActions}>
              <div style={paymentSummary(0)}>고객 환불 {fmt(linkedRefundTotal)}</div>
              <button type="button" style={miniBtn} onClick={addRefund}>+ 환불 추가</button>
            </div>
          </div>
          <div style={paymentListStyle}>
            {refunds.length === 0 ? (
              <div style={emptyBox}>환불내역이 없습니다.</div>
            ) : refunds.map((refund, index) => (
              <RefundRow
                key={refund.id || index}
                refund={refund}
                index={index}
                onChange={updateRefund}
                onRemove={removeRefund}
                style={refundRowStyle}
              />
            ))}
          </div>
        </section>
      </div>
      )}
    </Modal>
  );
}

const noteFieldWrap = { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 };
const noteLabel = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 };
const noteTextarea = {
  padding: '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--bg)',
  color: 'var(--text)',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  minHeight: 58,
  boxSizing: 'border-box',
  fontSize: 12,
  lineHeight: 1.3,
  resize: 'vertical',
};
